import { useCallback, useEffect, useRef } from 'react';

/**
 * Lets async searches ignore stale results.
 *
 * Call `startSearch()` at the start of each search. It returns `{ isLatest }`,
 * where `isLatest()` stays true until the next `startSearch()` call, or until
 * the component unmounts. Check it before every state update in async callbacks
 * (e.g. `.then`, `.catch`), and skip the update when it's false.
 * `startSearch` keeps the same identity across renders.
 *
 * @example
 * const { startSearch } = useSearchIdGuard();
 *
 * const runSearch = query => {
 *   const { isLatest } = startSearch();
 *   setStatus('searching');
 *   return fetchResults(query)
 *     .then(res => {
 *       if (!isLatest()) return;
 *       setResults(res);
 *       setStatus('idle');
 *     })
 *     .catch(() => {
 *       if (isLatest()) setStatus('error');
 *     });
 * };
 *
 * @returns {{ startSearch: () => { isLatest: () => boolean } }}
 */
export const useSearchIdGuard = () => {
  const latestSearchId = useRef(0);

  useEffect(() => {
    const counter = latestSearchId;
    return () => {
      counter.current += 1;
    };
  }, []);

  return {
    startSearch: useCallback(() => {
      // Searches can finish out of order. Each one is numbered so older ones can be ignored.
      latestSearchId.current += 1;
      // Closures keep each call's own copy of the ID. Later calls change the live counter but not the copy.
      const searchId = latestSearchId.current;

      // Compares the frozen value to the live one.
      return { isLatest: () => searchId === latestSearchId.current };
    }, []),
  };
};
